/**
 * The Investor Portals overview's Portals tab (#15170): coverage as the
 * client tracks it, one row per manager and portal pair, grouped by manager
 * (or, with the switch, by portal), under "Covering N managers across M
 * portals". Only client-healthy portals reach this component (the page
 * filters them), and it says nothing about a portal's state: no status
 * column, no pill, no failure wording. Choosing a row opens the portal's
 * page.
 *
 * The rules are in `browser/portals.ts`; this file only draws them.
 */
import React, { useMemo, useState } from "react";
import type { PortalService } from "@/components/FpFundBrain/Manage/Connections/InvestorPortalsRow";
import { ServiceFavicon } from "@/components/common/ServiceFavicon";
import { useIsPhoneViewport } from "@/hooks/useViewportTier";
import { useViewerTimeZone } from "@/hooks/useViewerTimeZone";
import { Segmented } from "@/components/FpPages/RMS/design";
import { GlobeIcon } from "@phosphor-icons/react";
import { RowPlaceholders, Section } from "./HomeSections";
import { rowActivationProps } from "./rowActivation";
import CutText from "./browser/CutText";
import {
  coverageGroups,
  coverageHeading,
  coverageRows,
  monthDayOf,
  type CoverageGroup,
  type CoverageGrouping,
  type CoverageRow,
} from "./browser/portals";

export interface PortalsOverviewProps {
  services: PortalService[];
  /** A row's click: open the portal's page, by the service's name. */
  onPickService: (service: string) => void;
  /** Inside a service the page's title block describes it, so the band
   *  shows nothing for it once it is resolved. */
  inService?: boolean;
  /** The service the page is inside, as the page resolved it; null while
   *  the services load, or when they could not be read. */
  service?: PortalService | null;
  /** The services read failed with no answer cached. */
  error?: boolean;
  /** The services read is still out: placeholders, no heading counts. */
  loading?: boolean;
  /** The host's frame is too narrow for the table: stacked blocks. */
  compact?: boolean;
}

/** A service's favicon, the globe when it has none: the rows' mark and,
 *  beside a portal page's heading, the page's. */
export const Mark: React.FC<{ service: PortalService; size?: number }> = ({
  service,
  size = 16,
}) => (
  <ServiceFavicon
    host={service.icon_host}
    label={service.service}
    size={size}
    fallback={
      <GlobeIcon
        size={size}
        aria-hidden
        className="shrink-0 text-[var(--fp-ink-3)]"
      />
    }
  />
);

const GROUPINGS: { key: CoverageGrouping; label: string }[] = [
  { key: "manager", label: "By manager" },
  { key: "portal", label: "By portal" },
];

const ROW_INTERACTION =
  "cursor-pointer outline-none hover:bg-[var(--fp-hover-row)] focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-[var(--fp-ink-3)]";

const listTh =
  "whitespace-nowrap px-3 py-2 text-left text-[11px] font-medium uppercase tracking-[0.04em] text-[var(--fp-ink-3)] border-b border-[var(--fp-border)]";
const listTd =
  "h-[var(--fp-row-h)] overflow-hidden text-ellipsis whitespace-nowrap px-3 py-0 align-middle text-[13px] text-[var(--fp-ink)]";

/** The portal cell: its real logo beside its name. */
const PortalCell: React.FC<{ service: PortalService }> = ({ service }) => (
  <span className="flex min-w-0 items-center gap-2">
    <span className="flex shrink-0">
      <Mark service={service} />
    </span>
    <CutText
      text={service.service}
      className="min-w-0 truncate text-[13px] text-[var(--fp-ink)]"
    />
  </span>
);

/** A pair's facts, as the row's cells and the phone block's line read
 *  them: the last document's date in the viewer's zone and the count. */
const useRowFacts = (row: CoverageRow) => {
  const zone = useViewerTimeZone();
  return {
    last:
      row.lastDocumentAt === null ? "—" : monthDayOf(row.lastDocumentAt, zone),
    count: `${row.documentCount.toLocaleString()} ${
      row.documentCount === 1 ? "document" : "documents"
    }`,
  };
};

const CoverageTableRow: React.FC<{
  row: CoverageRow;
  onPick: () => void;
}> = ({ row, onPick }) => {
  const facts = useRowFacts(row);
  return (
    <tr
      data-service={row.service.service}
      data-manager={row.manager?.name ?? ""}
      className={`border-b border-[var(--fp-border)] ${ROW_INTERACTION}`}
      {...rowActivationProps(onPick)}
    >
      <td className={listTd}>
        {row.manager === null ? (
          <span className="text-[var(--fp-ink-3)]">—</span>
        ) : (
          <span className="flex min-w-0">
            <CutText
              text={row.manager.name}
              className="min-w-0 truncate font-medium"
            />
          </span>
        )}
      </td>
      <td className={listTd}>
        <PortalCell service={row.service} />
      </td>
      <td
        className={`${listTd} text-[var(--fp-ink-2)] [font-variant-numeric:tabular-nums]`}
      >
        {facts.last}
      </td>
      <td
        className={`${listTd} text-right text-[var(--fp-ink-2)] [font-variant-numeric:tabular-nums]`}
      >
        {row.documentCount.toLocaleString()}
      </td>
    </tr>
  );
};

/** A group's heading row: the manager's name, or the portal's logo and
 *  name, over its rows. */
const GroupLabel: React.FC<{ group: CoverageGroup }> = ({ group }) => (
  <span className="flex min-w-0 items-center gap-2 text-[12.5px] font-medium text-[var(--fp-ink-2)]">
    {group.service !== null && <Mark service={group.service} size={14} />}
    <span className="min-w-0 truncate">{group.label}</span>
  </span>
);

const CoverageTable: React.FC<{
  groups: CoverageGroup[];
  onPickService: (service: string) => void;
}> = ({ groups, onPickService }) => (
  <div className="px-4">
    <table className="w-full table-fixed border-collapse">
      <thead>
        <tr>
          <th scope="col" className={`${listTh} w-[32%]`}>
            Manager
          </th>
          <th scope="col" className={`${listTh} w-[32%]`}>
            Portal
          </th>
          <th scope="col" className={`${listTh} w-[18%]`}>
            Last document
          </th>
          <th scope="col" className={`${listTh} text-right`}>
            Documents
          </th>
        </tr>
      </thead>
      {groups.map((group) => (
        <tbody key={group.key} role="rowgroup" aria-label={group.label}>
          <tr className="bg-[var(--fp-bg-well)]">
            <th
              scope="colgroup"
              colSpan={4}
              className="border-b border-[var(--fp-border)] px-3 py-1.5 text-left"
            >
              <GroupLabel group={group} />
            </th>
          </tr>
          {group.rows.map((row) => (
            <CoverageTableRow
              key={row.key}
              row={row}
              onPick={() => onPickService(row.service.service)}
            />
          ))}
        </tbody>
      ))}
    </table>
  </div>
);

const CoverageBlock: React.FC<{ row: CoverageRow; onPick: () => void }> = ({
  row,
  onPick,
}) => {
  const facts = useRowFacts(row);
  return (
    <li
      data-service={row.service.service}
      data-manager={row.manager?.name ?? ""}
      className={`flex flex-col gap-1 border-b border-[var(--fp-border)] px-4 py-2.5 ${ROW_INTERACTION}`}
      {...rowActivationProps(onPick)}
    >
      <span className="flex min-w-0 items-center gap-3">
        <span className="min-w-0 flex-1">
          <PortalCell service={row.service} />
        </span>
        {row.manager !== null && (
          <span className="min-w-0 max-w-[50%] truncate text-[13px] font-medium text-[var(--fp-ink)]">
            {row.manager.name}
          </span>
        )}
      </span>
      <span className="text-[12px] text-[var(--fp-ink-3)] [font-variant-numeric:tabular-nums]">
        <span className="whitespace-nowrap">{facts.count}</span>
        {" · "}
        <span className="whitespace-nowrap">Last {facts.last}</span>
      </span>
    </li>
  );
};

const CoverageBlocks: React.FC<{
  groups: CoverageGroup[];
  onPickService: (service: string) => void;
}> = ({ groups, onPickService }) => (
  <div className="flex flex-col gap-3">
    {groups.map((group) => (
      <div key={group.key} role="group" aria-label={group.label}>
        <div className="px-4 pb-1.5">
          <GroupLabel group={group} />
        </div>
        <ul className="m-0 list-none border-t border-[var(--fp-border)] p-0">
          {group.rows.map((row) => (
            <CoverageBlock
              key={row.key}
              row={row}
              onPick={() => onPickService(row.service.service)}
            />
          ))}
        </ul>
      </div>
    ))}
  </div>
);

const PortalsOverview: React.FC<PortalsOverviewProps> = ({
  services,
  onPickService,
  loading = false,
  inService = false,
  service = null,
  error = false,
  compact = false,
}) => {
  const phone = useIsPhoneViewport();
  const [grouping, setGrouping] = useState<CoverageGrouping>("manager");
  // Every listed portal, whatever the Documents tab's Source chip picks:
  // the chips narrow documents, not the coverage (#15170, R3).
  const rows = useMemo(() => coverageRows(services), [services]);
  const groups = useMemo(
    () => coverageGroups(rows, grouping),
    [rows, grouping]
  );

  // A resolved portal is described in the page's title block. Inside one
  // the read is still out (a failed read shows the overview instead).
  if (inService && service !== null) return null;
  if (inService) {
    return (
      <section aria-label="Portal" aria-busy={loading || undefined}>
        {loading && <RowPlaceholders />}
      </section>
    );
  }
  const toggle = (
    <Segmented
      aria-label="Group by"
      size="compact"
      options={GROUPINGS}
      value={grouping}
      onChange={setGrouping}
    />
  );
  if (error) {
    return (
      <Section title="Portals">
        <p className="m-0 px-4 text-[13px] text-[var(--fp-ink-3)]">
          Could not load the portals
        </p>
      </Section>
    );
  }
  if (loading) {
    return (
      <Section title="Portals">
        <RowPlaceholders />
      </Section>
    );
  }
  if (rows.length === 0) {
    return (
      <Section title="Portals">
        <p className="m-0 px-4 text-[13px] text-[var(--fp-ink-3)]">
          No investor portals yet
        </p>
      </Section>
    );
  }
  return (
    <Section title={coverageHeading(rows)} end={toggle}>
      {phone || compact ? (
        <CoverageBlocks groups={groups} onPickService={onPickService} />
      ) : (
        <CoverageTable groups={groups} onPickService={onPickService} />
      )}
    </Section>
  );
};

export default PortalsOverview;
